import { AiUsageDimension } from 'src/app/enums/aiUsageDimension.enum';
import { AiUsageRow, AiUsageVisibleRow } from 'src/app/models/aiUsageByUser.model';

export type AiUsageChangeTone = 'up' | 'down' | 'neutral' | 'new';

export interface AiUsageChange {
  tone: AiUsageChangeTone;
  percent: number | null;
  wasCredits: number | null;
}

export interface AiUsageExportRow {
  label: string;
  email: string;
  site: string;
  questions: number;
  reports: number;
  credits: number;
  share: number | null;
  change: number | null;
  activeDays: number;
  lastActive: Date | null;
}

const WAS_LABEL_MIN_PERCENT = 999;
const EXPORT_INDENT = '    ';
const MONTH_START_DAY = '01';
const FORMULA_PREFIX = /^[=+\-@\t\r]/;

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

export function currentMonthStart(now: Date = new Date()): string {
  return `${now.getUTCFullYear()}-${pad(now.getUTCMonth() + 1)}-${MONTH_START_DAY}`;
}

export function shiftMonth(monthStart: string, delta: number): string {
  const [year, month] = monthStart.split('-').map(Number);
  const shifted = new Date(Date.UTC(year, month - 1 + delta, 1));
  return `${shifted.getUTCFullYear()}-${pad(shifted.getUTCMonth() + 1)}-${MONTH_START_DAY}`;
}

export function formatMonthLabel(monthStart: string, locale: string): string {
  const [year, month] = monthStart.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString(locale, { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

export function describeChange(row: AiUsageRow): AiUsageChange {
  if (row.previousCredits === null || row.previousCredits === 0) {
    return { tone: 'new', percent: null, wasCredits: null };
  }
  const percent = Math.round(((row.credits - row.previousCredits) / row.previousCredits) * 100);
  if (percent === 0) {
    return { tone: 'neutral', percent, wasCredits: null };
  }
  const wasCredits = percent > WAS_LABEL_MIN_PERCENT ? row.previousCredits : null;
  return { tone: percent > 0 ? 'up' : 'down', percent, wasCredits };
}

export function formatChange(change: AiUsageChange, newLabel: string, formatWas: (credits: number) => string): string {
  if (change.percent === null) return newLabel;
  if (change.wasCredits !== null) return formatWas(change.wasCredits);
  return `${change.percent > 0 ? '+' : ''}${change.percent}%`;
}

export function formatSites(sites: string[]): string {
  if (!sites.length) return '';
  return sites.length === 1 ? sites[0] : `${sites[0]} +${sites.length - 1}`;
}

export function rowLabel(row: AiUsageRow, unknownLabel: string, locale: string): string {
  if (row.rowType === AiUsageDimension.Month) {
    return row.monthStart ? formatMonthLabel(row.monthStart, locale) : '';
  }
  return row.name ?? row.email ?? unknownLabel;
}

export function isGroupRow(row: AiUsageRow): boolean {
  return row.rowType === AiUsageDimension.Org || (row.rowType === AiUsageDimension.Month && row.children.length > 0);
}

export function neutralizeSpreadsheetFormula(text: string): string {
  return FORMULA_PREFIX.test(text) ? `'${text}` : text;
}

export function buildExportRows(visibleRows: AiUsageVisibleRow[], unknownLabel: string, locale: string): AiUsageExportRow[] {
  return visibleRows.map(({ row, depth }) => ({
    label: `${EXPORT_INDENT.repeat(depth)}${neutralizeSpreadsheetFormula(rowLabel(row, unknownLabel, locale))}`,
    email: neutralizeSpreadsheetFormula(row.email ?? ''),
    site: neutralizeSpreadsheetFormula(row.sites.join(', ')),
    questions: row.questionCount,
    reports: row.reportCount,
    credits: row.credits,
    share: row.share,
    change: row.previousCredits === null ? null : describeChange(row).percent,
    activeDays: row.activeDays,
    lastActive: row.lastActive ? new Date(row.lastActive) : null,
  }));
}
